import React, {useState, useEffect} from 'react';
import Wrapper from '@components/Wrapper';
import Label from '@components/Label';
import LogoMark from '@components/LogoMark';
import Logo from '@components/Logo';
import twitterIcon from '@icons/twitterIcon';
import twitterIconDark from '@icons/twitterIconDark';
import linkedinIcon from '@icons/linkedinIcon';
import linkedinIconDark from '@icons/linkedinIconDark';
import instagramIcon from '@icons/instagramIcon';
import instagramIconDark from '@icons/instagramIconDark';
import facebookIcon from '@icons/facebookIcon';
import facebookIconDark from '@icons/facebookIconDark';
import twitterIconWhite from '@icons/twitterIconWhite';
import linkedinIconWhite from '@icons/linkedinIconWhite';
import instagramIconWhite from '@icons/instagramIconWhite';
import facebookIconWhite from '@icons/facebookIconWhite';
import youtubeIconWhite from '@icons/youtubeIconWhite';
import youtubeIconDark from '@icons/youtubeIconDark';
import menuDropdownIconBlack from '@icons/menuDropdownIconBlack';
import { Link, useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import GdprBadge from '../../images/g2/gdpr-compliant.svg';
import iso27001 from '../../images/g2/iso-27001.svg';
import iso27701 from '../../images/g2/iso-27701.svg';
import bidpHighPerformerCanada from '../../images/g2/VisitorIdentification_HighPerformer_EMEA_HighPerformer.svg';
import siHighPerformerAsiaPacific from '../../images/g2/VisitorIdentification_BestSupport_Enterprise_QualityOfSupport.svg';
import visitorIdent from '../../images/g2/SalesIntelligence_HighPerformer_HighPerformer.svg';
import siHighPerformerGermany from '../../images/g2/SalesIntelligence_EasiestSetup_Small-Business_EaseOfSetup.svg';
import siHighPerformerSummer from '../../images/g2/MarketingAccountIntelligence_FastestImplementation_GoLiveTime.svg';
import siLeaderEurope from '../../images/g2/LeadIntelligence_HighPerformer_UnitedKingdom_HighPerformer.svg';
import viSBLeader from '../../images/g2/LeadIntelligence_HighPerformer_Europe_HighPerformer.svg';
import easOfAdmin from '../../images/g2/LeadIntelligence_EasiestAdmin_Mid-Market_EaseOfAdmin.svg';

import * as styles from './style.module.scss';
const {
  footer,
  footer__info,
  footer__caption,
  footer__socials,
  footer__icon,
  footer__categories,
  footer__category,
  footer__links,
  footer__link,
  footer__whitetext,
  footer__bottom,
  footer__copyrights,
  footer__wrapper,
  footer__logo,
  footer__mid,
  footer__midLogo,
  footer__midWrap,
  footer__label,
  footer__labeltop,
  footer__careers,
  footer__headingTopSpacing,
  footer__formSection,
  footer__siteInfo,
  footer__subscribeform,
  footer__subscribeFormLeft,
  footer__subscribeFormRight,
  footer__copyrightsWrap,
  footer__g2Badges,
  footer__g2BadgeMove,
  footer__isStream,
  footer__hideForm,
  reversed,
} = styles;

const Footer = ({ classes, LogoComponent, whiteText, hideSubscribeForm }) => {
  const { language, path } = useI18next();
  const { t } = useTranslation();
  const isUsPage = path.startsWith('/us/');
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null);

  const toggleAccordion = (index) => {
    if (windowWidth <= 1139) {
      setOpenAccordionIndex((prev) => (prev === index ? null : index));
    }
  };

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (hideSubscribeForm) return;

    const script = document.createElement('script');
    script.src = `https://dealfront.activehosted.com/f/embed.php?id=${
      language === 'en' ? 105 : 107
    }`;
    script.async = true;

    document.querySelector('.active-campaign-form-footer').appendChild(script);

    return () => {
      // Cleanup script when component unmounts
      const existingScript = document.querySelector(
        '.active-campaign-form-footer script'
      );
      if (existingScript) {
        document
          .querySelector('.active-campaign-form-footer')
          .removeChild(existingScript);
      }
    };
  }, [language, hideSubscribeForm]);

  const menu = [
    {
      label: t('footer_product_heading'),
      links: [
        {
          label: t('footer_platform'),
          href: '/platform/',
        },
        {
          label: t('nav_webvisitors_page'),
          href: '/web-visitors/',
        },
        {
          label: t('nav_target_page'),
          href: '/target/',
        },
        {
          label: t('nav_connect_page'),
          href: '/connect/',
        },
        {
          label: t('footer_datacare'),
          href: '/datacare/',
        },
        {
          label: t('nav_promote_page'),
          href: '/promote/',
        },
      ],
      label2: t('footer_company_heading'),
      links2: [
        {
          label: t('nav_about_page'),
          href: '/about/',
        },
        {
          label: t('nav_careers_page'),
          href: '/careers/',
          lang: 'en',
        },
      ],
    },
    {
      label: t('footer_features_heading'),
      links: [
        {
          label: t('footer_features_chrome_extension'),
          href: '/browser-extension/',
        },
        {
          label: t('nav_sales_buyer_intent_signals_page'),
          href: '/solutions/buying-intent-signals/',
        },
        {
          label: t('footer_features_company_to_ip'),
          href: '/ip-ranges-api/',
        },
        {
          label: t('footer_features_ip_to_company'),
          href: '/ip-enrich-api/',
        },
        {
          label: t('footer_features_integrations'),
          href: '/integrations/',
        },
      ],
      label2: t('footer_support_heading'),
      links2: [
        {
          label: 'Academy',
          href: '/resources/academy/',
        },
        {
          label: 'Help Center',
          href:
            language === 'de'
              ? 'https://help.dealfront.com/de/'
              : 'https://help.dealfront.com/en/',
          target: '_blank',
          rel: 'noreferrer',
          external: true,
        },
        {
          label: t('nav_label_contact'),
          href: '/contact/',
        },
      ],
    },
    {
      label: t('nav_label_solutions'),
      links: [
        {
          label: t('footer_solutions_marketing'),
          href: '/solutions/marketing/',
        },
        {
          label: t('footer_solutions_sales'),
          href: '/solutions/sales/',
        },
        {
          label: 'Sales Intelligence',
          href: '/solutions/sales-prospecting/',
        },
        {
          label: 'Web Visitor Identification',
          href: '/solutions/web-visitor-tracking/',
        },
        {
          label: t('nav_sales_advertising_page'),
          href: '/solutions/b2b-advertising/',
        },
      ],
    },
    {
      label: t('footer_data_heading'),
      links: [
        {
          label: t('footer_data_our'),
          href: '/our-data/',
        },
        {
          label: t('footer_data_gdpr'),
          href: '/gdpr/',
        },
        {
          label: t('nav_information_security'),
          href: '/security/',
        },
      ],
    },
    {
      label: t('footer_resources_heading'),
      links: [
        {
          label: t('all_resources'),
          href: '/resources/all/',
        },
        {
          label: t('nav_label_blog'),
          href: '/blog/',
        },
        {
          label: t('footer_content_playbooks'),
          href: '/resources/playbooks/',
        },
        {
          label: t('nav_label_stream'),
          href: '/resources/stream/',
        },
        {
          label: t('footer_content_sales_stoic'),
          href: '/resources/the-sales-stoic/',
        },
        {
          label: t('nav_label_guides'),
          href: '/guides/',
        },
        {
          label: t('footer_customers_case_studies'),
          href: '/customers/',
        },
        {
          label: t('footer_content_newsletters'),
          href: '/resources/newsletters/',
        },
        {
          label: t('footer_community_partners'),
          href: '/partners/',
        },
        {
          label: t('footer_content_tam_calc'),
          href: 'https://www.dealfront.com/tools/tam-calculator/',
        },
      ],
    },
    /*{
      label: t('footer_community_heading'),
      links: [
        {
          label: t('footer_community_events'),
          href: '/',
        },
        {
          label: t('footer_community_owned'),
          href: '/',
        },
      ],
    },
    {
      label: t('footer_customers_heading'),
      links: [
        {
          label: t('footer_customers_reviews'),
          href: '/',
        },
      ],
    },    */
  ];

  const mobileMenu = [
    // Custom order of sections for mobile
    {
      key: 'product',
      label: t('footer_product_heading'),
      links: menu[0].links,
    },
    {
      key: 'features',
      label: t('footer_features_heading'),
      links: menu[1].links,
    },
    {
      key: 'solutions',
      label: t('nav_label_solutions'),
      links: menu[2].links,
    },
    {
      key: 'data',
      label: t('footer_data_heading'),
      links: menu[3].links,
    },
    {
      key: 'resources',
      label: t('footer_resources_heading'),
      links: menu[4].links,
    },
    {
      key: 'company',
      label: t('footer_company_heading'),
      links: menu[0].links2,
    },
    {
      key: 'support',
      label: t('footer_support_heading'),
      links: menu[1].links2,
    },
  ];

  const socials = [
    {
      title: 'LinkedIn',
      icon: whiteText ? linkedinIconWhite : linkedinIconDark,
      href: 'https://www.linkedin.com/company/dealfront/',
    },
    {
      title: 'Instagram',
      icon: whiteText ? instagramIconWhite : instagramIconDark,
      href: 'https://www.instagram.com/getdealfront/',
    },
    {
      title: 'Facebook',
      icon: whiteText ? facebookIconWhite : facebookIconDark,
      href: 'https://www.facebook.com/getdealfront/',
    },
    {
      title: 'Twitter',
      icon: whiteText ? twitterIconWhite : twitterIconDark,
      href: 'https://twitter.com/getdealfront/',
    },
    {
      title: 'YouTube',
      icon: whiteText ? youtubeIconWhite : youtubeIconDark,
      href: 'https://www.youtube.com/@dealfront',
    },
  ];

  const bottomLinks = [
    {
      label: 'Sitemap',
      href: language === 'de' ? '/de/sitemap/' : '/sitemap/',
    },
    {
      label: t('footer_terms_conditions'),
      href:
        language === 'de'
          ? '/de/terms-and-conditions/'
          : '/terms-and-conditions/',
    },
    {
      label: 'Security',
      href: language === 'de' ? '/de/security/' : '/security/',
    },
    {
      label: t('footer_terms_privacy_notice'),
      href: language === 'de' ? '/de/privacy-notice/' : '/privacy-notice/',
    },
    {
      label: t('footer_terms_cookie'),
      href:
        language === 'de'
          ? '/de/cookies-and-tracking/'
          : '/cookies-and-tracking/',
    },
  ];

  return (
    <>
      <section
        className={`${footer__formSection} ${
          hideSubscribeForm ? footer__hideForm : ''
        }`}
      >
        <Wrapper>
          <div
            id="footer-subscribe-form"
            className={`${footer__subscribeform}`}
          >
            <div className="active-campaign-form-container footer-ac-form background-transparent in-content">
              <div className={footer__subscribeFormLeft}>
                <h3
                  dangerouslySetInnerHTML={{ __html: t('join_our_newsletter') }}
                />
                <p
                  dangerouslySetInnerHTML={{
                    __html: t('join_our_newsletter_subheading'),
                  }}
                />
              </div>
              <div
                className={`${footer__subscribeFormRight} active-campaign-form-footer`}
              >
                <div
                  className={language === 'en' ? '_form_105' : '_form_107'}
                  id="ac-form"
                ></div>
              </div>
            </div>
          </div>
        </Wrapper>
      </section>
      <footer
        className={`${footer} ${classes || ''} ${
          hideSubscribeForm ? footer__isStream : ''
        }`}
      >
        <Wrapper classes={footer__wrapper}>
          {/*<div className={footer__info}>
          {LogoComponent ? (
            LogoComponent
          ) : (
            <LogoMark
              classes={footer__logo}
              href={'/'}
              title={t('nav_home_page')}
            />
          )}

          <div className={footer__socials}>
            {socials.map(({ title, icon, href }, index) => (
              <a
                key={`footer-${index}-link-${index}`}
                className={footer__icon}
                href={href}
                target="_blank"
                rel="noreferrer"
                title={`Visit our ${title}.`}
              >
                {icon}
              </a>
            ))}
          </div>
        </div> */}
          <div className={footer__categories}>
            {windowWidth <= 1139 ? (
              // 📱 MOBILE VIEW: Use custom-sorted mobileMenu array
              mobileMenu.map((section, index) => {
                const isOpen = openAccordionIndex === index;

                return (
                  <div key={`mobile-menu-${index}`} className={footer__category}>
                    <button
                      className={`${footer__label} ${whiteText ? footer__whitetext : ''} ${isOpen ? 'open' : ''}`}
                      onClick={() => toggleAccordion(index)}
                    >
                      {section.label}
                      {menuDropdownIconBlack}
                    </button>
                    <div
                      style={{
                        display: isOpen ? 'flex' : 'none',
                        flexDirection: 'column',
                      }}
                    >
                      {section.links?.map(({ label, href, target, rel, external, lang }, subIndex) =>
                        external ? (
                          <a
                            key={`mobile-link-${index}-${subIndex}`}
                            className={`${footer__link} ${whiteText ? footer__whitetext : ''}`}
                            href={href}
                            target={target}
                            rel={rel}
                          >
                            {label}
                            {href === '/careers/' && (
                              <span className={footer__careers}>Hiring!</span>
                            )}
                            {(href === '/resources/playbooks/' ||
                              href === '/resources/stream/' ||
                              href === '/resources/the-sales-stoic/' ||
                              href === '/resources/newsletters/') && (
                              <span className={footer__careers}>{t("nav_new")}</span>
                            )}
                          </a>
                        ) : (
                          <Link
                            key={`mobile-link-${index}-${subIndex}`}
                            language={lang || language}
                            className={`${footer__link} ${whiteText ? footer__whitetext : ''}`}
                            to={href}
                            target={target}
                            rel={rel}
                          >
                            {label}
                            {href === '/careers/' && (
                              <span className={footer__careers}>Hiring!</span>
                            )}
                            {(href === '/resources/playbooks/' ||
                              href === '/resources/stream/' ||
                              href === '/resources/the-sales-stoic/' ||
                              href === '/resources/newsletters/') && (
                              <span className={footer__careers}>{t("nav_new")}</span>
                            )}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              // 💻 DESKTOP VIEW: Keep original structure with label and label2 combined
              menu.map(({ label, links, label2, links2 }, index) => (
                <div key={`menu-${index}`} className={footer__category}>
                  {label && (
                    <Label classes={`${footer__label} ${footer__headingTopSpacing} ${whiteText ? footer__whitetext : ''}`}>
                      {label}
                    </Label>
                  )}
                  {links?.map(({ label, href, target, rel, external, lang }, subIndex) =>
                    external ? (
                      <a
                        key={`menu-${index}-link-${subIndex}`}
                        className={`${footer__link} ${whiteText ? footer__whitetext : ''}`}
                        href={href}
                        target={target}
                        rel={rel}
                      >
                        {label}
                        {href === '/careers/' && <span className={footer__careers}>Hiring!</span>}
                        {(href === '/resources/playbooks/' ||
                          href === '/resources/stream/' ||
                          href === '/resources/the-sales-stoic/' ||
                          href === '/resources/newsletters/') && (
                          <span className={footer__careers}>{t("nav_new")}</span>
                        )}
                      </a>
                    ) : (
                      <Link
                        key={`menu-${index}-link-${subIndex}`}
                        language={lang || language}
                        className={`${footer__link} ${whiteText ? footer__whitetext : ''}`}
                        to={href}
                        target={target}
                        rel={rel}
                      >
                        {label}
                        {href === '/careers/' && <span className={footer__careers}>Hiring!</span>}
                        {(href === '/resources/playbooks/' ||
                          href === '/resources/stream/' ||
                          href === '/resources/the-sales-stoic/' ||
                          href === '/resources/newsletters/') && (
                          <span className={footer__careers}>{t("nav_new")}</span>
                        )}
                      </Link>
                    )
                  )}

                  {label2 && (
                    <Label classes={`${footer__label} ${footer__headingTopSpacing} ${whiteText ? footer__whitetext : ''}`}>
                      {label2}
                    </Label>
                  )}
                  {links2?.filter(Boolean).map(({ label, href, target, rel, external, lang }, subIndex) =>
                    external ? (
                      <a
                        key={`menu-${index}-link2-${subIndex}`}
                        className={`${footer__link} ${whiteText ? footer__whitetext : ''}`}
                        href={href}
                        target={target}
                        rel={rel}
                      >
                        {label}
                        {href === '/careers/' && <span className={footer__careers}>Hiring!</span>}
                        {(href === '/resources/playbooks/' ||
                          href === '/resources/stream/' ||
                          href === '/resources/the-sales-stoic/' ||
                          href === '/resources/newsletters/') && (
                          <span className={footer__careers}>{t("nav_new")}</span>
                        )}
                      </a>
                    ) : (
                      <Link
                        key={`menu-${index}-link2-${subIndex}`}
                        language={lang || language}
                        className={`${footer__link} ${whiteText ? footer__whitetext : ''}`}
                        to={href}
                        target={target}
                        rel={rel}
                      >
                        {label}
                        {href === '/careers/' && <span className={footer__careers}>Hiring!</span>}
                        {(href === '/resources/playbooks/' ||
                          href === '/resources/stream/' ||
                          href === '/resources/the-sales-stoic/' ||
                          href === '/resources/newsletters/') && (
                          <span className={footer__careers}>New</span>
                        )}
                      </Link>
                    )
                  )}
                </div>
              ))
            )}
          </div>

        </Wrapper>
        <Wrapper classes={footer__midWrap}>
          <div
            className={`${footer__mid} ${whiteText ? footer__whitetext : ''}`}
          >
            <div className={footer__midLogo}>
              {LogoComponent ? (
                LogoComponent
              ) : (
                <Logo
                  href={`${isUsPage ? '/us' : '/'}`}
                  classes={`${footer__logo}`}
                  title={t('nav_home_page')}
                />
              )}
            </div>
            <nav className={footer__links}>
              {bottomLinks.map(({ label, href, additionalClasses }, index) => (
                <a
                  key={`bottomLink-${index}`}
                  className={`footer__link ${
                    additionalClasses === undefined ? '' : additionalClasses
                  }`}
                  href={href}
                >
                  {label}
                </a>
              ))}
              <button
                className={`footer__link`}
                onClick={() => window?.cookiehub?.openSettings()}
              >
                {t('footer_cookie_settings')}
              </button>
            </nav>
          </div>
        </Wrapper>
        <Wrapper>
          <div
            className={`${footer__bottom} ${
              whiteText ? footer__whitetext : ''
            }`}
          >
            <div className={footer__siteInfo}>
              <div className={footer__socials}>
                {socials.map(({ title, icon, href }, index) => (
                  <a
                    key={`footer-${index}-link-${index}`}
                    className={footer__icon}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    title={`Visit our ${title}.`}
                  >
                    {icon}
                  </a>
                ))}
              </div>
              <div className={footer__copyrightsWrap}>
                <small className={footer__copyrights}>
                  © {new Date().getFullYear()} Dealfront Group GmbH.{' '}
                  {t('footer_copyright')}
                </small>
              </div>
            </div>
            <ul className={footer__g2Badges}>
              <li>
                <img src={GdprBadge} alt="GDPR" />
              </li>
              <li>
                <img src={iso27001} alt="ISO 27001" />
              </li>
              <li>
                <img src={iso27701} alt="ISO 27701" />
              </li>
              <li>
                <img
                  src={bidpHighPerformerCanada}
                  alt="High Performer Canada 2025"
                />
              </li>
              <li>
                <img
                  src={siHighPerformerAsiaPacific}
                  alt="High Performer Asia Pacific 2025"
                />
              </li>
              <li>
                <img src={visitorIdent} alt="Momentum Leader 2025" />
              </li>
              <li>
                <img
                  src={siHighPerformerGermany}
                  alt="High Performer Germany 2025"
                />
              </li>
              <li>
                <img
                  src={siHighPerformerSummer}
                  alt="High Performer Summer 2025"
                />
              </li>
              <li>
                <img src={siLeaderEurope} alt="Leader Europe Summer 2025" />
              </li>
              <li>
                <img src={viSBLeader} alt="Leader Small Business Summer 2025" />
              </li>
              <li>
                <img src={easOfAdmin} alt="Ease of admin 2025" />
              </li>
            </ul>
          </div>
        </Wrapper>
      </footer>
      <style>
        {`            
      #footer-subscribe-form [class*="active-campaign-form-container"] [id*="ac-form"] [class*="_inline-form"] [class*="_row"] label,
      #footer-subscribe-form [class*="active-campaign-form-container"] [id*="ac-form"] [class*="_inline-form"] [class*="_row"] span {
          color: #828A9A !important;
          line-height: 1.2;
          margin-right: 12px;
      }

      #footer-subscribe-form [class*="active-campaign-form-container"] [id*="ac-form"] [class*="_checkbox-radio"] {
        transform: translateY(-15px);
      }
      #footer-subscribe-form [class*=active-campaign-form-container] [class*=_button-wrapper] button {
          color: var(--color-white)!important;
          background: var(--color-blue)!important;
          font-family: var(--font-family-bold) !important;
      }

      #footer-subscribe-form [class*=active-campaign-form-container] [class*="_form-content"] input[type="text"] {
          border: 1px solid #B1BCCF!important;
          border-radius: 6px!important;
          padding: 0 15px;
      }

      @media(min-width: 1024px) {
          .active-campaign-form-container.in-content ._form-content ._button-wrapper._full_width {
            max-width: 188px;
          }
      }
      `}
      </style>
    </>
  );
};

export default Footer;
